import { type RefObject, useEffect } from "react";

/** A field of the fee schedule category editor that a page can ask to focus (#6097). */
export type FeeScheduleCategoryEditorField = "fee" | "teamMember";

/**
 * A page's request that the category editor focus one of its fields. Each new `request` is one more
 * ask, so pressing the same issue twice focuses the field twice.
 */
export type FeeScheduleCategoryEditorFocus = {
  readonly field: FeeScheduleCategoryEditorField;
  readonly request: number;
};

/** The request for `field`, or undefined when the editor was asked for another field or none. */
export function focusRequestFor(
  focus: FeeScheduleCategoryEditorFocus | undefined,
  field: FeeScheduleCategoryEditorField,
): number | undefined {
  return focus?.field === field ? focus.request : undefined;
}

/**
 * Focuses the first control in `container` on each new `request`.
 *
 * The first control, because a field's group is where its fix starts: the Fee group of a deferred fee
 * opens with "Let … set charge?", since its amount is not drawn until that box is unticked. A frame
 * later, because the editor may still be sliding in, and a browser does not focus what it has not
 * drawn.
 */
export function useFocusWhenAsked(container: RefObject<HTMLElement | null>, request: number | undefined): void {
  useEffect(() => {
    if (!request) return;
    const frame = requestAnimationFrame(() => {
      const control = container.current?.querySelector<HTMLElement>(FOCUSABLE_CONTROL);
      control?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [container, request]);
}

// A redline's display is a control too: focusing it opens its input (#6624).
const FOCUSABLE_CONTROL =
  "input:not([type='hidden']):not(:disabled), textarea:not(:disabled), button:not(:disabled), [role='textbox'][tabindex='0']";
